import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService
} from '@vendure/admin-ui/core';
import { Subject, takeUntil } from 'rxjs';

import { GoogleMerchantCenterSettings, GOOGLE_MERCHANT_CENTER_DATA_SOURCE } from '../types';

@Component({
    selector: 'gmc-settings-form',
    template: `
    <div class="integration-panel">
      <div class="integration-header">
        <div class="integration-info">
          <div class="integration-title">Google Merchant Center</div>
          <div class="integration-description">Product syncing for Google Shopping.</div>
        </div>
        <clr-toggle-wrapper class="toggle-right">
          <input
            type="checkbox"
            clrToggle
            name="gmcEnabled"
            [formControl]="gmcEnabledControl"
          />
        </clr-toggle-wrapper>
      </div>

      <div *ngIf="gmcEnabledControl.value && siteUrl" class="integration-fields">
        <vdr-form-field label="Product Data Source Feed URL">
          <input type="text" readonly [formControl]="gmcDataSourceControl" />
        </vdr-form-field>
      </div>
    </div>
    `,
    styles: [`
      .integration-panel {
        padding: 16px;
        border: 1px solid var(--color-component-border-100);
        border-radius: 4px;
        background: white;
      }

      .integration-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }

      .integration-info {
        flex: 1;
      }

      .integration-title {
        font-weight: 600;
        font-size: 14px;
        color: var(--color-text-100);
        margin-bottom: 4px;
      }

      .integration-description {
        font-size: 13px;
        color: var(--color-text-300);
        line-height: 1.4;
      }

      .toggle-right {
        margin-left: 16px;
        flex-shrink: 0;
      }

      .integration-fields {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--color-component-border-100);
      }

      .integration-fields vdr-form-field {
        margin-bottom: 12px;
      }

      .integration-fields vdr-form-field:last-child {
        margin-bottom: 0;
      }
    `],
    standalone: true,
    imports: [SharedModule],
})
export class GMCSettingInputComponent
    implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    readonly: boolean;

    private _config: StringCustomFieldConfig;
    get config(): StringCustomFieldConfig {
        return this._config;
    }
    // Keep siteUrl in sync with config so the feed URL updates when channel changes
    set config(config: StringCustomFieldConfig) {
        this._config = config;
        this.siteUrl = config.ui?.config?.siteUrl ?? '';
        // If already enabled, update the data source field when siteUrl changes
        if (this.gmcSettings?.enabled && this.siteUrl) {
            this.gmcDataSourceControl.setValue(this.siteUrl + GOOGLE_MERCHANT_CENTER_DATA_SOURCE, { emitEvent: false });
            this.changeDetector.markForCheck();
        }
    }

    formControl: FormControl;

    // Google Merchant Center controls
    gmcEnabledControl = new FormControl();
    gmcDataSourceControl = new FormControl();

    gmcSettings: GoogleMerchantCenterSettings;
    siteUrl = '';

    private destroy$ = new Subject<void>();

    constructor(
        private notificationService: NotificationService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        const defaultGmcSettings: GoogleMerchantCenterSettings = {
            enabled: false,
        };
        // Initial value from parent control
        this.applyExternalValue(this.formControl.value, defaultGmcSettings);

        // React to external changes (e.g. when parent formControl is updated on channel switch)
        this.formControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                this.applyExternalValue(value, defaultGmcSettings);
            });
    }

    ngAfterViewInit() {
        // Subscribe to Google Merchant Center control changes
        this.gmcEnabledControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.onGmcEnabledChange);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyExternalValue(value: GoogleMerchantCenterSettings | null, defaultSettings: GoogleMerchantCenterSettings) {
        const settings = value ?? defaultSettings;
        this.gmcSettings = { ...settings };

        if (this.gmcSettings.enabled && this.siteUrl) {
            this.gmcDataSourceControl.setValue(this.siteUrl + GOOGLE_MERCHANT_CENTER_DATA_SOURCE, { emitEvent: false });
        } else {
            this.gmcDataSourceControl.setValue('', { emitEvent: false });
        }

        // Update form controls without emitting to prevent feedback loops
        this.gmcEnabledControl.setValue(this.gmcSettings.enabled, { emitEvent: false });

        this.changeDetector.markForCheck();
    }

    // GMC enabled change handler
    private onGmcEnabledChange = (value: boolean) => {
        if (this.gmcSettings.enabled !== value) {
            this.gmcSettings.enabled = value;
            if (this.siteUrl && value) {
                this.gmcDataSourceControl.setValue(this.siteUrl + GOOGLE_MERCHANT_CENTER_DATA_SOURCE);
            } else {
                this.gmcDataSourceControl.setValue('');
            }
            this.updateValue();
            this.changeDetector.markForCheck();
        }
    };

    private updateValue() {
        // Do not emit to avoid feeding back into our own valueChanges subscription
        this.formControl.setValue(this.gmcSettings, { emitEvent: false });
        this.formControl.markAsDirty();
    }
}